/**
 * Formatting + date helpers. General infrastructure: nothing in here knows
 * about any particular view.
 *
 * Every formatter here is total: it takes anything the API can hand us
 * (`null`, `undefined`, a division that produced `NaN` or `Infinity`) and
 * returns a string that is safe to render. No screen in this app is allowed
 * to put `NaN`, `undefined`, `Infinity` or an unrounded float on screen, and
 * the only reliable way to guarantee that is to funnel every number through
 * one place rather than trusting each call site to remember. Reach for `num`,
 * `kg`, `signedKg` and friends rather than a bare `.toFixed()`, even where the
 * value is provably finite today — the invariant is the reason this file
 * exists, and "provably finite" ages badly.
 *
 * Motion constants live in `./motion`, not here: an easing curve is not a
 * number format, and `EASE_OUT` sitting in this file was the reason the two
 * modules imported each other.
 *
 * `kg`, `signedKg`, `tonnage`, `bodyWeight`, `bodyWeightParts`,
 * `signedBodyWeight` and `heightCm` delegate their actual unit conversion and
 * magnitude formatting to `./units` — this file's job for those seven is only
 * totality (`null`/`undefined`/`NaN`/`Infinity` → `DASH`). The dependency
 * runs one way, `format.ts` → `units.ts`; see that module's header for why
 * reversing it is not an option.
 */
import {
  formatBodyWeight,
  formatBodyWeightParts,
  formatHeight,
  formatLoad,
  formatLoadParts,
  toLb,
  type BodyWeightUnit,
  type HeightStyle,
  type HeightUnit,
  type LoadUnit,
} from './units'

/**
 * Placeholder for a value that genuinely does not exist yet. A middle dot,
 * matching the rest of the app (see the old `ProgramTransition` readout) and
 * avoiding the em dash the copy rules ban.
 */
export const DASH = '·'

/** Returns the number only when it is a real, finite number. */
export function finite(n: number | null | undefined): number | null {
  return typeof n === 'number' && Number.isFinite(n) ? n : null
}

/** `45` → `"45.0"`, anything unusable → the placeholder. */
export function num(n: number | null | undefined, digits = 1): string {
  const v = finite(n)
  return v === null ? DASH : v.toFixed(digits)
}

/**
 * `45` → `"45 kg"`, `82.5` → `"82.5 kg"`, `82.5` in `lb` → `"182 lb"`.
 * Delegates in full to `units.ts`'s `formatLoad` — D4 is explicit that `kg`
 * keeps its existing `cleanWeight` behaviour (`80`, not `80.0`) and `lb`
 * renders whole. The backend made the identical call for the coach prompt's
 * own kg rendering (`"36.0kg"` → `"36 kg"`, ruling R6 on the backend branch);
 * answering the same question differently here — three renderings of one
 * value across web, iOS and the coach — is exactly what `units.ts` exists to
 * prevent as a symbol-for-symbol mirror across all three.
 *
 * `unit` is required, and every call site was converted to pass one during
 * Task 3's `npx tsc -b` sweep — `tsc -b` is pinned at 0 (CLAUDE.md's Global
 * constraints), which is what keeps a call site from regressing back to a
 * single argument unnoticed.
 */
export function kg(n: number | null | undefined, unit: LoadUnit): string {
  const v = finite(n)
  return v === null ? DASH : formatLoad(v, unit)
}

/**
 * `kg`, split into a value and a trailing unit word — totality wrapper around
 * `units.ts`'s `formatLoadParts`, for a set-grid cell or a hero metric that
 * renders as two separate JSX nodes. `unit` is `''` alongside the placeholder
 * `value`, same shape as `bodyWeightParts`'s unusable-number case.
 */
export function kgParts(n: number | null | undefined, unit: LoadUnit): { value: string; unit: string } {
  const v = finite(n)
  return v === null ? { value: DASH, unit: '' } : formatLoadParts(v, unit)
}

/**
 * Body mass in the user's unit — totality wrapper around `units.ts`'s
 * `formatBodyWeight` (the `st` compound included). Delegates in full, same
 * as `kg`.
 */
export function bodyWeight(n: number | null | undefined, unit: BodyWeightUnit): string {
  const v = finite(n)
  return v === null ? DASH : formatBodyWeight(v, unit)
}

/**
 * `bodyWeight`, split into a value and a trailing unit word — totality
 * wrapper around `units.ts`'s `formatBodyWeightParts`, for a hero figure and
 * a small dim unit that render as two separate JSX nodes. `unit` is `''`
 * alongside the placeholder `value`, same shape as the finite case's empty
 * `unit` for the stone compound — a call site already has to check for an
 * empty `unit` before rendering it, so the unusable-number case reuses that
 * check rather than adding a second one.
 */
export function bodyWeightParts(
  n: number | null | undefined,
  unit: BodyWeightUnit,
): { value: string; unit: string } {
  const v = finite(n)
  return v === null ? { value: DASH, unit: '' } : formatBodyWeightParts(v, unit)
}

/**
 * Height in the user's unit — totality wrapper around `units.ts`'s
 * `formatHeight`. No prior caller, same as `bodyWeight`: delegates in full.
 */
export function heightCm(
  n: number | null | undefined,
  unit: HeightUnit,
  style?: HeightStyle,
): string {
  const v = finite(n)
  return v === null ? DASH : formatHeight(v, unit, style)
}

/**
 * Seconds as `m:ss` — `50` → `"0:50"`, `150` → `"2:30"`.
 *
 * A hold is read as a duration, not as a count, so it never renders as a bare
 * number. Lives here rather than beside the set grid because three screens
 * format the same seconds (the grid, the day total, the Home briefing) and
 * two formatters is how the same 90 seconds becomes "1:30" on one and "90" on
 * the next.
 */
export function formatDuration(sec: number | null | undefined): string {
  const v = finite(sec)
  if (v === null) return DASH
  const total = Math.max(0, Math.round(v))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

/**
 * `12345.6` → `"12,346 kg"` — tonnage is never interesting below the kilo, so
 * this always rounds to a whole number regardless of unit (unlike `kg`,
 * there is no `cleanKg`-style precision distinction to make: an aggregate
 * this large is never going to land on a value worth a decimal). `lb`
 * converts via `units.ts`'s `toLb` before rounding.
 */
export function tonnage(n: number | null | undefined, unit: LoadUnit): string {
  const v = finite(n)
  if (v === null) return DASH
  if (unit === 'lb') return `${Math.round(toLb(v)).toLocaleString('en-US')} lb`
  return `${Math.round(v).toLocaleString('en-US')} kg`
}

/**
 * `tonnage`, split into a value and a trailing unit word — for a hero figure
 * rendered as two JSX nodes (a large `clamp()`-sized numeral, a small dim
 * unit suffix), same shape and reason as `kgParts`. **Not `kgParts`
 * itself**: an aggregate this large needs `tonnage`'s en-US thousands
 * grouping (`"1,323"`, never `"1323"`) — `kgParts`/`cleanKg` has no grouping
 * at all, correct for a single set's weight but wrong for a session total
 * (see `tonnage`'s own doc comment on the German-locale bug this already
 * fixed once for the un-split string).
 */
export function tonnageParts(
  n: number | null | undefined,
  unit: LoadUnit,
): { value: string; unit: string } {
  const v = finite(n)
  if (v === null) return { value: DASH, unit: '' }
  return unit === 'lb'
    ? { value: Math.round(toLb(v)).toLocaleString('en-US'), unit: 'lb' }
    : { value: Math.round(v).toLocaleString('en-US'), unit: 'kg' }
}

/**
 * `1.04` → `"+1.0 kg"`, `-2.5` → `"−2.5 kg"` (true minus sign, not a hyphen),
 * `1` → `"+1 kg"` (D4's `cleanWeight`, same as `kg`). Shared with the Body
 * section, where almost every number is a signed change.
 *
 * Reuses `kg`/`formatLoad` for the magnitude and prepends the sign, so a
 * signed load follows the exact same per-unit precision rule the unsigned
 * one does — `kg` drops a whole number's trailing `.0`, `lb` always renders
 * whole.
 */
export function signedKg(n: number | null | undefined, unit: LoadUnit): string {
  const v = finite(n)
  if (v === null) return DASH
  const magnitude = formatLoad(Math.abs(v), unit)
  if (v > 0) return `+${magnitude}`
  if (v < 0) return `−${magnitude}`
  return magnitude
}

/**
 * `1.04` → `"+1.0 kg"`, `-2.5` → `"−2.5 kg"`, `1.2` in `st` → `"+3 lb"`.
 * `signedKg`'s exact shape, for a body-mass delta instead of a load one — the
 * sign sits outside the magnitude.
 *
 * **`st` does not stay a compound here (coordinator ruling R10).** A +1.2 kg
 * gain is +2.6 lb, and rendered as a compound that is `"+0 st 3 lb"` — nobody
 * says that; people say "up three pounds". So for a *delta*, `st` renders
 * whole signed pounds instead of the compound. The *absolute* value keeps the
 * compound (`formatBodyWeight(84.2, 'st')` is still `"13 st 4 lb"`): stone is
 * a unit for stating a weight, not for stating a change in one.
 *
 * Added for Task 5: three independent sites (a Journey body-section verdict
 * sentence, a dashboard scan-strip subtitle, a composition delta rail) each
 * needed to sign a body-weight delta, the same "prose, not labels" shape the
 * brief already named for `CompositionTab.tsx`. One formatter here instead of
 * three inlined sign-then-format blocks, matching `signedKg`'s own reason for
 * existing.
 */
export function signedBodyWeight(n: number | null | undefined, unit: BodyWeightUnit): string {
  const v = finite(n)
  if (v === null) return DASH
  const magnitude =
    unit === 'st' ? `${Math.round(toLb(Math.abs(v)))} lb` : formatBodyWeight(Math.abs(v), unit)
  if (v > 0) return `+${magnitude}`
  if (v < 0) return `−${magnitude}`
  return magnitude
}

/** `12.34` → `"+12%"`, `-3.2` → `"−3%"` (true minus sign, not a hyphen). */
export function signedPct(n: number | null | undefined, digits = 0): string {
  const v = finite(n)
  if (v === null) return DASH
  const rounded = v.toFixed(digits)
  if (v > 0) return `+${rounded}%`
  // toFixed already carries the sign for negatives; swap the ASCII hyphen for
  // a typographic minus so it lines up with the tabular figures around it.
  return `${rounded.replace('-', '−')}%`
}

/** Percentage change from `first` to `latest`; null when it cannot be computed. */
export function changePct(
  first: number | null | undefined,
  latest: number | null | undefined,
): number | null {
  const a = finite(first)
  const b = finite(latest)
  if (a === null || b === null || a === 0) return null
  return ((b - a) / a) * 100
}

/** `"2026-01-28"` → `"Jan 28"`. Returns the raw input if it is not a date. */
export function shortDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * `"2026-01-28"` → `"Jan 28, 2026"`.
 *
 * Unlike `shortDate`, anything unparseable yields the placeholder rather than
 * the raw input. This one is used for dates the reader meets on their own line
 * — a record's date, a milestone — where a raw ISO fragment would read as a
 * bug, whereas `shortDate` feeds chart axes, where a missing tick reads worse.
 * The year is never dropped: the Records book spans whole years, and "Jan 28"
 * alone cannot say which one.
 */
export function fullDate(iso: string | null | undefined): string {
  if (!iso) return DASH
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(d.getTime())) return DASH
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** `"2026-01-28"` → `"Jan 2026"`. */
export function monthYear(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

/**
 * ISO date of the Monday that owns this date. Calendar weeks in this product
 * are always Monday–Sunday (see the domain notes), so every weekly bucket in
 * the section has to agree on that or the volume panel would disagree with
 * the rest of the app by a day.
 */
export function mondayOf(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10)
  const offset = (d.getDay() + 6) % 7 // Mon = 0
  d.setDate(d.getDate() - offset)
  return toIsoDate(d)
}

/** Local-time `YYYY-MM-DD` (never `toISOString`, which shifts across UTC). */
export function toIsoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Adds `weeks` to an ISO date, returning a new ISO date. */
export function addWeeks(iso: string, weeks: number): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  d.setDate(d.getDate() + weeks * 7)
  return toIsoDate(d)
}

/**
 * Age in whole years for someone born on `dob`, as of `refDate` (defaults to
 * today). A birthday doesn't "count" until it actually happens that year, so
 * a plain year subtraction overcounts by one for everyone whose birthday
 * hasn't yet occurred in the reference year — the month/day check below backs
 * the age off by one in exactly that case. Both `dob` and `refDate` accept
 * either an ISO date string or a `Date`.
 */
export function ageFromDob(dob: string | Date, refDate: string | Date = new Date()): number {
  const birth = typeof dob === 'string' ? new Date(`${dob.slice(0, 10)}T00:00:00`) : dob
  const ref = typeof refDate === 'string' ? new Date(`${refDate.slice(0, 10)}T00:00:00`) : refDate
  let age = ref.getFullYear() - birth.getFullYear()
  const m = ref.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && ref.getDate() < birth.getDate())) age--
  return age
}

/** `"chest"` / `"Chest"` → `"Chest"`; nullish → null. */
export function titleCase(s: string | null | undefined): string | null {
  if (!s) return null
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}
